import React, { useContext, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { format, isSameDay, startOfDay, isAfter, startOfWeek } from "date-fns";
import {
  FaRegCheckCircle,
  FaRegBell,
  FaDumbbell,
  FaBook,
  FaBed,
  FaBrain,
  FaHeart,
  FaPlay,
  FaQuoteLeft,
  FaTimes,
} from "react-icons/fa";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

// --- Firebase & Auth Imports ---
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  collection,
  doc,
  updateDoc,
  onSnapshot,
  setDoc,
  increment,
  getDoc,
} from "firebase/firestore";
import noti from "../assets/notiSound.mp3";

// --- Context Imports ---
import { DashboardContext } from "../contexts/DashboardContext";
import { RemindersContext } from "../contexts/RemindersContext";
import { WorkHoursContext } from "../contexts/WorkHoursContext";

// Icon mapping for Habits
const icons = {
  FaDumbbell,
  FaBook,
  FaBed,
  FaBrain,
  FaHeart,
};

// Motivational Quotes for Start of Day
const MOTIVATIONAL_QUOTES = [
  "The secret of getting ahead is getting started. – Mark Twain",
  "Don't watch the clock; do what it does. Keep going. – Sam Levenson",
  "The way to get started is to quit talking and begin doing. – Walt Disney",
  "Your time is limited, don't waste it living someone else's life. – Steve Jobs",
  "The future depends on what you do today. – Mahatma Gandhi",
  "It's not about having time, it's about making time. – Unknown",
  "Every morning we are born again. What we do today matters most. – Buddha",
  "The only way to do great work is to love what you do. – Steve Jobs",
  "Success is the sum of small efforts, repeated day in and day out. – Robert Collier",
  "Today is your opportunity to build the tomorrow you want. – Ken Poirot",
  "The morning breeze has secrets to tell you. Do not go back to sleep. – Rumi",
  "Win the morning, win the day. – Tim Ferriss",
  "Each morning we are born again. What we do today is what matters most. – Buddha",
  "The sun himself is weak when he first rises, and gathers strength and courage as the day gets on. – Charles Dickens",
  "Morning is an important time of day, because how you spend your morning can often tell you what kind of day you are going to have. – Lemony Snicket",
  "When you arise in the morning, think of what a precious privilege it is to be alive – to breathe, to think, to enjoy, to love. – Marcus Aurelius",
  "Every day is a new beginning. Treat it that way. Stay away from what might have been, and look at what can be. – Marsha Petrie Sue",
  "Each day, I come in with a positive attitude, trying to get better. – Stefon Diggs",
  "Write it on your heart that every day is the best day in the year. – Ralph Waldo Emerson",
  "The morning is the rudder of the day. – Henry Wadsworth Longfellow",
];

const toDate = (val) => {
  if (!val) return null;
  if (typeof val.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  try {
    return new Date(val);
  } catch {
    return null;
  }
};

const useRealHabits = () => {
  const { userId } = useAuth();
  const [habits, setHabits] = useState([]);
  const selectedDate = new Date();
  const dateKey = format(selectedDate, "yyyy-MM-dd");
  const weekKey = format(
    startOfWeek(selectedDate, { weekStartsOn: 1 }),
    "yyyy-MM-dd"
  );
  const selectedDayName = format(selectedDate, "EEEE");

  useEffect(() => {
    if (!userId) return;
    const col = collection(db, "users", userId, "habits");
    const unsub = onSnapshot(col, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHabits(data);
    });
    return () => unsub();
  }, [userId]);

  const dayHabits = habits
    .filter((h) => {
      const type =
        h.recurrenceType ||
        (h.days && h.days.length ? "SPECIFIC_DAYS" : "DAILY");
      if (type === "FLEXIBLE_WEEKLY") return true;
      const hdays = h.days || [];
      if (!hdays || hdays.length === 0) return true;
      return hdays.includes(selectedDayName);
    })
    .map((h) => {
      const type =
        h.recurrenceType ||
        (h.days && h.days.length ? "SPECIFIC_DAYS" : "DAILY");
      let isCompleted = false;

      if (type === "FLEXIBLE_WEEKLY") {
        const currentCount = h.weeklyLogs?.[weekKey] || 0;
        const target = h.targetCount || 1;
        isCompleted =
          Boolean(h.completedDates?.[dateKey]) || currentCount >= target;
      } else {
        isCompleted = Boolean(h.completedDates?.[dateKey]);
      }

      const IconComponent = icons[h.icon] || icons.FaDumbbell || FaDumbbell;

      return {
        ...h,
        isCompleted,
        Icon: IconComponent,
      };
    });

  const toggleHabit = async (habitId) => {
    if (!userId) return;
    const habit = habits.find((h) => h.id === habitId);
    if (!habit) return;

    const habitRef = doc(db, "users", userId, "habits", habitId);
    const done = habit.completedDates?.[dateKey] || false;
    const type =
      habit.recurrenceType ||
      (habit.days && habit.days.length ? "SPECIFIC_DAYS" : "DAILY");

    if (type === "FLEXIBLE_WEEKLY") {
      const currentCount = habit.weeklyLogs?.[weekKey] || 0;
      if (!done && habit.targetCount && currentCount >= habit.targetCount)
        return;

      try {
        if (!done) {
          await updateDoc(habitRef, {
            [`completedDates.${dateKey}`]: true,
            [`weeklyLogs.${weekKey}`]: increment(1),
          });
        } else {
          await updateDoc(habitRef, {
            [`completedDates.${dateKey}`]: false,
            [`weeklyLogs.${weekKey}`]: increment(-1),
          });
        }
      } catch (err) {
        console.error(err);
      }
    } else {
      try {
        await updateDoc(habitRef, { [`completedDates.${dateKey}`]: !done });
      } catch (err) {
        console.error(err);
      }
    }
  };

  return { dayHabits, toggleHabit };
};

const getTodaysTasks = (tasks) => {
  const today = new Date();
  const todayKey = format(today, "yyyy-MM-dd");
  const dayNameFull = format(today, "EEEE");
  const todayStart = startOfDay(today);

  return tasks
    .filter((t) => {
      const tCreatedDate = toDate(t.createdAt) || new Date(0);
      const tDate = t.date ? toDate(t.date) : null;

      if (isAfter(startOfDay(tCreatedDate), todayStart)) return false;

      if (t.recurring) {
        const isScheduledForToday =
          !t.days || t.days.length === 0 || t.days.includes(dayNameFull);
        return !!isScheduledForToday;
      }

      if (!t.date) {
        if (t.sticky) {
          const completedFromKey = t.completedFrom || null;
          if (completedFromKey && todayKey > completedFromKey) return false;
          return true;
        }
        return isSameDay(tCreatedDate, today);
      }

      if (tDate) return isSameDay(tDate, today);
      return false;
    })
    .map((task) => {
      let isCompleted = false;
      if (task.recurring || task.sticky) {
        isCompleted = task.completedDates?.[todayKey] ?? false;
      } else {
        isCompleted = task.completed;
      }
      return { ...task, isCompleted };
    });
};

// ---------------- STYLED COMPONENTS ----------------
const popIn = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to { opacity: 1; transform: scale(1); }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: 99999999999999999999999999999999999;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(5px);
  padding: 0;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const SlideContainer = styled.div`
  max-height: 85vh;
  width: 700px;
  background: var(--secondaryBackgroundColor);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  z-index: 10000000000000;
  padding: 2.5rem;
  overflow-y: auto;
  color: var(--fontPrimaryColor);

  border-radius: 16px;
  animation: ${popIn} 0.4s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.1);

  /* Mobile styles */
  @media (max-width: 768px) {
    width: 100%;
    max-height: 90vh;
    padding: 1.5rem;
    border-radius: 20px 20px 0 0;
    animation: ${slideUp} 0.3s ease-out;
    margin: 0;
  }

  @media (max-width: 480px) {
    padding: 1.2rem;
    /* max-height: 85vh; */
    border-radius: 16px 16px 0 0;
    margin-top: 300px;
  }

  @media (max-width: 360px) {
    padding: 1rem;
    max-height: 85vh;
  }

  /* Hide scrollbar but keep functionality */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--thirdBackgroundColor);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--primaryColor);
    border-radius: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 25px;
  background: none;
  border: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: var(--fontSecondaryColor);
  transition: all 0.2s;
  padding: 5px;
  z-index: 1000000000000000000000000000000;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--fontPrimaryColor);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
    font-size: 1.5rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    width: 36px;
    height: 36px;
  }

  @media (max-width: 480px) {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
    font-size: 1.3rem;
  }
`;

const Header = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: var(--primaryColor);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    gap: 10px;
    margin-top: 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1.3rem;
    gap: 8px;
  }

  @media (max-width: 360px) {
    font-size: 1.2rem;
    flex-direction: column;
    gap: 6px;
  }
`;

const DateText = styled.p`
  font-size: 1rem;
  color: var(--fontSecondaryColor);
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
`;

const QuoteSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.1),
    rgba(147, 51, 234, 0.1)
  );
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  border-left: 4px solid var(--primaryColor);
  position: relative;
  animation: fadeIn 1s ease-out;

  @media (max-width: 768px) {
    padding: 1.2rem;
    margin-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }

  @media (max-width: 360px) {
    padding: 0.8rem;
    border-left-width: 3px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  color: var(--primaryColor);
  opacity: 0.3;
  font-size: 1.2rem;

  @media (max-width: 480px) {
    top: 10px;
    left: 10px;
    font-size: 1rem;
  }

  @media (max-width: 360px) {
    display: none;
  }
`;

const QuoteText = styled.p`
  font-size: 1.1rem;
  color: var(--fontPrimaryColor);
  font-style: italic;
  text-align: center;
  margin: 0;
  padding: 0 2rem;
  line-height: 1.5;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 1rem;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 0.8rem;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    padding: 0 0.5rem;
  }
`;

const QuoteAuthor = styled.p`
  font-size: 0.9rem;
  color: var(--fontSecondaryColor);
  text-align: center;
  margin-top: 0.8rem;
  font-style: normal;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 0.6rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--thirdBackgroundColor);

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    padding-bottom: 0.8rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--fontPrimaryColor);
  margin-bottom: 0.8rem;
  border-left: 4px solid var(--primaryColor);
  padding-left: 12px;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    gap: 8px;
    padding-left: 10px;
    margin-bottom: 0.7rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    gap: 6px;
    padding-left: 8px;
    border-left-width: 3px;
    margin-bottom: 0.6rem;
  }

  @media (max-width: 360px) {
    font-size: 0.95rem;
    gap: 4px;
  }
`;

const ItemList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const Item = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--thirdBackgroundColor);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  border-left: 5px solid
    ${({ isCompleted }) => (isCompleted ? "#22c55e" : "transparent")};

  &:hover {
    background: var(--forthBackgroundColor);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    padding: 10px;
    gap: 10px;
    border-left-width: 4px;
  }

  @media (max-width: 480px) {
    padding: 8px;
    gap: 8px;
    border-left-width: 3px;
  }

  @media (max-width: 360px) {
    padding: 6px;
    gap: 6px;
  }
`;

const ItemContent = styled.span`
  flex-grow: 1;
  font-size: 1rem;
  text-decoration: ${({ isCompleted }) =>
    isCompleted ? "line-through" : "none"};
  color: ${({ isCompleted }) =>
    isCompleted ? "var(--fontSecondaryColor)" : "var(--fontPrimaryColor)"};
  transition: all 0.3s;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
`;

const IconWrapper = styled.div`
  color: ${({ isCompleted }) =>
    isCompleted ? "#22c55e" : "var(--primaryColor)"};
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    width: 22px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    width: 20px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    width: 18px;
  }
`;

const EmptyState = styled.p`
  font-size: 1rem;
  color: var(--fontSecondaryColor);
  padding: 20px 0;
  text-align: center;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 15px 0;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 12px 0;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
    padding: 10px 0;
  }
`;

const StartDayButton = styled.button`
  background: ${({ disabled }) =>
    disabled
      ? "var(--fontSecondaryColor)"
      : "linear-gradient(135deg, var(--primaryColor), #8b5cf6)"};
  color: white;
  padding: 1.2rem 2rem;
  border-radius: 14px;
  border: none;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.3s ease;
  width: 100%;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);

  &:not(:disabled):hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
    filter: brightness(1.1);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    margin-bottom: 1.2rem;
    gap: 10px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.2rem;
    font-size: 1rem;
    margin-bottom: 1rem;
    gap: 8px;
    border-radius: 10px;
  }

  @media (max-width: 360px) {
    padding: 0.8rem 1rem;
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
    gap: 6px;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 25px;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--thirdBackgroundColor);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    margin-bottom: 1.2rem;
    padding-bottom: 1.2rem;
  }

  @media (max-width: 480px) {
    gap: 15px;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }
`;

const RingWrapper = styled.div`
  width: 120px;
  height: 120px;
  transition: all 0.5s ease-out;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 480px) {
    width: 90px;
    height: 90px;
  }

  @media (max-width: 360px) {
    width: 80px;
    height: 80px;
  }

  .CircularProgressbar-path {
    transition: stroke-dashoffset 0.5s ease 0s;
    stroke-linecap: round;
  }

  .CircularProgressbar-text {
    font-weight: 800;
    font-size: 28px;
    fill: var(--fontPrimaryColor);

    @media (max-width: 768px) {
      font-size: 24px;
    }

    @media (max-width: 480px) {
      font-size: 20px;
    }

    @media (max-width: 360px) {
      font-size: 18px;
    }
  }

  .CircularProgressbar-trail {
    stroke: var(--thirdBackgroundColor);
  }
`;

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;

  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
  }
`;

const ProgressTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primaryColor);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }

  @media (max-width: 360px) {
    font-size: 1rem;
  }
`;

const ProgressStats = styled.p`
  font-size: 1rem;
  color: var(--fontSecondaryColor);
  margin: 8px 0 0 0;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-top: 6px;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }

  @media (max-width: 360px) {
    font-size: 0.85rem;
  }
`;

const ItemTypeBadge = styled.span`
  font-size: 0.8rem;
  color: var(--fontSecondaryColor);
  opacity: 0.7;
  background: rgba(0, 0, 0, 0.1);
  padding: 3px 10px;
  border-radius: 12px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 2px 8px;
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 2px 6px;
    display: none; /* Hide on very small screens */
  }
`;

const DailyProgressRing = ({ totalItems, completedItems }) => {
  if (totalItems === 0) {
    return (
      <ProgressContainer>
        <ProgressInfo>
          <ProgressTitle>Ready to Begin!</ProgressTitle>
          <ProgressStats>
            Add your first Habit, Task, or Reminder to start tracking your day.
          </ProgressStats>
        </ProgressInfo>
      </ProgressContainer>
    );
  }

  const percentage = Math.round((completedItems / totalItems) * 100);
  const primaryColor =
    getComputedStyle(document.documentElement).getPropertyValue(
      "--primaryColor"
    ) || "#3b82f6";
  const successColor = "#22c55e";

  return (
    <ProgressContainer>
      <RingWrapper>
        <CircularProgressbar
          value={percentage}
          text={`${percentage}%`}
          styles={buildStyles({
            rotation: 0.25,
            strokeLinecap: "round",
            textSize: "28px",
            pathTransitionDuration: 0.5,
            pathColor: percentage === 100 ? successColor : primaryColor,
            textColor:
              percentage === 100 ? successColor : "var(--fontPrimaryColor)",
            trailColor: "var(--thirdBackgroundColor)",
          })}
        />
      </RingWrapper>
      <ProgressInfo>
        <ProgressTitle>Daily Progress</ProgressTitle>
        <ProgressStats>
          <strong>{completedItems}</strong> of <strong>{totalItems}</strong>{" "}
          items completed today.
          {percentage === 100 && (
            <span
              style={{
                color: successColor,
                fontWeight: "bold",
                display: "block",
                marginTop: "8px",
                fontSize: "0.9rem",
              }}
            >
              🎉 Perfect day! All goals achieved!
            </span>
          )}
        </ProgressStats>
      </ProgressInfo>
    </ProgressContainer>
  );
};

// Hook to check if user has seen agenda today
const useDailyAgendaCheck = () => {
  const { userId } = useAuth();
  const [hasSeenAgendaToday, setHasSeenAgendaToday] = useState(false);
  const [loading, setLoading] = useState(true);
  const todayKey = format(new Date(), "yyyy-MM-dd");

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const checkAgendaStatus = async () => {
      try {
        const agendaDocRef = doc(db, "users", userId, "agendaStatus", "daily");
        const agendaDoc = await getDoc(agendaDocRef);

        if (agendaDoc.exists()) {
          const lastSeenDate = agendaDoc.data().lastSeen;
          if (lastSeenDate) {
            const lastSeen = toDate(lastSeenDate);
            const today = new Date();

            // Check if user has seen agenda today
            const hasSeenToday = lastSeen && isSameDay(lastSeen, today);
            setHasSeenAgendaToday(hasSeenToday);

            // If not seen today, update the record
            if (!hasSeenToday) {
              await updateDoc(agendaDocRef, {
                lastSeen: new Date(),
                lastSeenDate: todayKey,
              });
            }
          } else {
            // First time seeing agenda
            await setDoc(agendaDocRef, {
              lastSeen: new Date(),
              lastSeenDate: todayKey,
            });
            setHasSeenAgendaToday(false);
          }
        } else {
          // First time user - create document
          await setDoc(agendaDocRef, {
            lastSeen: new Date(),
            lastSeenDate: todayKey,
          });
          setHasSeenAgendaToday(false);
        }
      } catch (error) {
        console.error("Error checking agenda status:", error);
        setHasSeenAgendaToday(false);
      } finally {
        setLoading(false);
      }
    };

    checkAgendaStatus();
  }, [userId, todayKey]);

  return { hasSeenAgendaToday, loading };
};

export default function AgendaPopup({ isOpen, onClose }) {
  const { tasks: allTasks, toggleTask } = useContext(DashboardContext);
  const { todayReminders, toggleReminder } = useContext(RemindersContext);
  const { isRunning, handleStart } = useContext(WorkHoursContext);

  const { dayHabits, toggleHabit } = useRealHabits();
  const { hasSeenAgendaToday, loading } = useDailyAgendaCheck();

  // Get a random motivational quote
  const [motivationalQuote, setMotivationalQuote] = useState("");

  useEffect(() => {
    // Select a random quote
    const randomIndex = Math.floor(Math.random() * MOTIVATIONAL_QUOTES.length);
    const quoteWithAuthor = MOTIVATIONAL_QUOTES[randomIndex];

    // Split quote and author
    const [quote, author] = quoteWithAuthor.split(" – ");
    setMotivationalQuote({ quote, author });
  }, []);

  // Auto-open on first login of the day
  useEffect(() => {
    if (!loading && !hasSeenAgendaToday && !isOpen) {
      // We'll handle this in parent component - just return the status
    }
  }, [loading, hasSeenAgendaToday, isOpen]);

  if (!isOpen) return null;

  const todaysTasks = getTodaysTasks(allTasks);

  const handleToggleTask = (taskId, clientId) => {
    if (toggleTask) {
      toggleTask(taskId, clientId);
    } else {
      console.log("Toggle task function missing in DashboardContext");
    }
  };

  const playCheckSound = () => {
    const audio = new Audio(noti);
    audio.volume = 0.9;
    audio.play().catch(() => {});
  };

  const handleStartDayClick = () => {
    if (isRunning) return;
    handleStart();
  };

  const agendaTasks = todaysTasks.map((t) => ({
    id: t.id,
    title: t.text || t.title,
    isCompleted: t.isCompleted,
    type: "Task",
    icon: FaRegCheckCircle,
    clientId: t.clientId,
    handler: () => handleToggleTask(t.id, t.clientId),
  }));

  const agendaHabits = dayHabits.map((h) => ({
    id: h.id,
    title: h.name,
    isCompleted: h.isCompleted,
    type: "Habit",
    icon: h.Icon || FaDumbbell,
    handler: () => toggleHabit(h.id),
  }));

  const agendaReminders = todayReminders.map((r) => ({
    id: r.id,
    title: r.title,
    isCompleted: r.isCompleted,
    type: "Reminder",
    icon: FaRegBell,
    handler: () => toggleReminder(r.id),
  }));

  const combinedAgenda = [
    ...agendaHabits,
    ...agendaTasks,
    ...agendaReminders,
  ].sort((a, b) =>
    a.isCompleted === b.isCompleted ? 0 : a.isCompleted ? 1 : -1
  );

  const totalItems = combinedAgenda.length;
  const completedItems = combinedAgenda.filter((i) => i.isCompleted).length;

  return (
    <Overlay onClick={onClose}>
      <SlideContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>
        <Header>
          <FaRegCheckCircle />
          Today's Agenda
        </Header>
        <DateText>{format(new Date(), "EEEE, MMMM do, yyyy")}</DateText>

        {/* Motivational Quote Section */}
        {motivationalQuote && (
          <QuoteSection>
            <QuoteIcon>
              <FaQuoteLeft />
            </QuoteIcon>
            <QuoteText>"{motivationalQuote.quote}"</QuoteText>
            {motivationalQuote.author && (
              <QuoteAuthor>– {motivationalQuote.author}</QuoteAuthor>
            )}
          </QuoteSection>
        )}

        {/* Start Day Button */}
        <StartDayButton
          onClick={handleStartDayClick}
          disabled={isRunning}
          title={
            isRunning
              ? "The work timer is already running."
              : "Click to start the work timer."
          }
        >
          <FaPlay />
          {isRunning
            ? "The Work Hour Timer is Running!"
            : "Let's Start the Day!"}
        </StartDayButton>

        <DailyProgressRing
          totalItems={totalItems}
          completedItems={completedItems}
        />

        <Section>
          <SectionTitle>
            Today's Focus ({combinedAgenda.filter((i) => !i.isCompleted).length}{" "}
            remaining)
          </SectionTitle>

          {combinedAgenda.length > 0 ? (
            <ItemList>
              {combinedAgenda.map((item) => (
                <Item
                  key={`${item.type}-${item.id}`}
                  isCompleted={item.isCompleted}
                  onClick={() => {
                    const wasCompleted = item.isCompleted;
                    item.handler();
                    if (!wasCompleted) {
                      playCheckSound();
                    }
                  }}
                >
                  <IconWrapper isCompleted={item.isCompleted}>
                    {typeof item.icon === "function" ||
                    typeof item.icon === "object" ? (
                      <item.icon />
                    ) : (
                      <FaRegCheckCircle />
                    )}
                  </IconWrapper>
                  <ItemContent isCompleted={item.isCompleted}>
                    {item.title}
                  </ItemContent>
                  <ItemTypeBadge>{item.type}</ItemTypeBadge>
                </Item>
              ))}
            </ItemList>
          ) : (
            <EmptyState>
              Your schedule is clear for today. Time to relax or add some goals!
            </EmptyState>
          )}
        </Section>

        <Section>
          <SectionTitle>Today's Summary</SectionTitle>
          <p
            style={{
              fontSize: "1rem",
              color: "var(--fontSecondaryColor)",
              paddingLeft: "12px",
              lineHeight: "1.6",
            }}
          >
            <strong>{agendaHabits.length}</strong> Habits •{" "}
            <strong>{agendaTasks.length}</strong> Tasks •{" "}
            <strong>{agendaReminders.length}</strong> Reminders
            <br />
            <span
              style={{ fontSize: "0.9rem", marginTop: "5px", display: "block" }}
            >
              {completedItems} completed • {totalItems - completedItems}{" "}
              remaining
            </span>
          </p>
        </Section>
      </SlideContainer>
    </Overlay>
  );
}

// Export the hook to check if agenda should be shown
export const useShouldShowAgenda = () => {
  const { hasSeenAgendaToday, loading } = useDailyAgendaCheck();
  return { shouldShow: !loading && !hasSeenAgendaToday, loading };
};
